import { DistributionType, DistributionTypes } from "~/types";
import { DISTRIBUTION_TYPE_TOOLTIPS } from "~/consts";
import { ButtonGroup, ButtonGroupOption } from "../ButtonGroup";

// // // //

export function getDistributionOptions(props: {
    distributedEnabled: boolean;
    representedEnabled: boolean;
}): Array<ButtonGroupOption> {
    const { distributedEnabled, representedEnabled } = props;

    const distributedOption: ButtonGroupOption = {
        label: "Distributed",
        value: DistributionTypes.Distributed,
        testID: "distribution-select--distributed",
    };

    const representedOption: ButtonGroupOption = {
        label: "Represented",
        value: DistributionTypes.Represented,
        testID: "distribution-select--represented",
    };

    const allOption: ButtonGroupOption = {
        label: "All",
        value: DistributionTypes.All,
        testID: "distribution-select--all",
    };

    const distributionOptions: ButtonGroupOption[] = [];

    // Only add options that are enabled
    if (distributedEnabled) {
        distributionOptions.push(distributedOption);
    }
    if (representedEnabled) {
        distributionOptions.push(representedOption);
    }
    if (distributedEnabled && representedEnabled) {
        distributionOptions.push(allOption);
    }

    return distributionOptions;
}

/**
 * A select component with animated sliding indicator for platform distribution types
 */
export function DistributionSelect(props: {
    value: DistributionType;
    onChange: (value: DistributionType) => void;
    counts?: {
        distributed?: number;
        represented?: number;
        all?: number;
    };
    includeDescription?: boolean;
    includeTooltips?: boolean;
    label?: string | null;
    disabled?: boolean;
    enabledOptions?: {
        distributed: boolean;
        represented: boolean;
    };
}) {
    const {
        value,
        onChange,
        counts,
        includeDescription = true,
        includeTooltips = false,
        label = "Asset Type",
        disabled = false,
        enabledOptions = {
            distributed: true,
            represented: true,
        },
    } = props;

    const distributedOption: ButtonGroupOption = {
        label: "Distributed",
        value: DistributionTypes.Distributed,
        count: counts?.distributed,
        testID: "distribution-select--distributed",
        tooltip: includeTooltips
            ? DISTRIBUTION_TYPE_TOOLTIPS[DistributionTypes.Distributed]
            : undefined,
    };

    const representedOption: ButtonGroupOption = {
        label: "Represented",
        value: DistributionTypes.Represented,
        count: counts?.represented,
        testID: "distribution-select--represented",
        tooltip: includeTooltips
            ? DISTRIBUTION_TYPE_TOOLTIPS[DistributionTypes.Represented]
            : undefined,
    };

    const allOption: ButtonGroupOption = {
        label: "All",
        value: DistributionTypes.All,
        count: counts?.all,
        testID: "distribution-select--all",
        tooltip: includeTooltips
            ? DISTRIBUTION_TYPE_TOOLTIPS[DistributionTypes.All]
            : undefined,
    };

    const distributionOptions: ButtonGroupOption[] = [];

    // Only add options that are enabled
    if (enabledOptions.distributed) {
        distributionOptions.push(distributedOption);
    }
    if (enabledOptions.represented) {
        distributionOptions.push(representedOption);
    }
    if (enabledOptions.distributed && enabledOptions.represented) {
        distributionOptions.push(allOption);
    }

    // Return null if there's only one option to show
    if (distributionOptions.length < 3) {
        return null;
    }

    const selectElement = (
        <ButtonGroup
            title={label}
            options={distributionOptions}
            value={value}
            disabled={disabled}
            onValueChange={(value) => onChange(value as DistributionType)}
        />
    );

    if (!includeDescription) {
        return selectElement;
    }

    return (
        <div>
            {selectElement}
            <p className="text-muted-foreground text-xs leading-relaxed w-full min-h-12 mt-2">
                {value === DistributionTypes.All ? (
                    <>All assets, distributed and represented.</>
                ) : value === DistributionTypes.Distributed ? (
                    <>
                        Distributed assets use the blockchain as a distribution
                        layer, enabling onchain investors to subscribe, hold,
                        and manage assets directly through their own wallets or
                        custodians.
                    </>
                ) : (
                    <>
                        Represented assets use the blockchain as a recordkeeping
                        layer, enhancing transparency, reconciliation, and
                        operational efficiency without enabling onchain investor
                        transfer or distribution.
                    </>
                )}
            </p>
        </div>
    );
}
